import React, { useEffect, useMemo, useRef } from 'react';
import { Form, Select } from 'antd';
import s from './s.module.less';
import AdvanceNotice from 'components/Calendar/AdvanceNotice';
import { ReminderSettings } from 'types/calendarSetting';

const { Option } = Select;

const ADVANCE_OPTIONS = [
    { value: 'none', label: 'No restriction' },
    { value: '1', label: '1 hour' },
    { value: '2', label: '2 hours' },
    { value: '3', label: '3 hours' },
    { value: '4', label: '4 hours' },
    { value: 'other', label: 'Custom' },
];

interface IPorps {
    formId?: string;
    onSaveStep?: () => void;
    setNextButtonDisabled?: (disabled: boolean) => void;
    initValue?: ReminderSettings;
}

const Step3: React.FC<IPorps> = ({ formId, onSaveStep, setNextButtonDisabled, initValue }) => {
    const [form] = Form.useForm();
    const advanceNoticeRef = useRef<any>(null);
    const advanceNoticeVal = Form.useWatch('advanceNotice', form);
    const showNotice = useMemo(() => {
        return advanceNoticeVal && advanceNoticeVal >= 2;
    }, [advanceNoticeVal]);

    const handleFinish = async () => {
        await advanceNoticeRef.current.onUpdate();
        onSaveStep?.();
    };

    useEffect(() => {
        setNextButtonDisabled?.(!advanceNoticeVal && advanceNoticeVal !== 0);
    }, [advanceNoticeVal, setNextButtonDisabled]);

    return (
        <div className={s.wrap}>
            <div className={s.content}>
                <div className={s.header}>
                    <h1>When should patients book before their visit?</h1>
                    <div className={s.headerDescription}>
                        Set how far in advance patients must book an appointment. This ensures you have enough time to
                        prepare.
                    </div>
                </div>
                {showNotice ? (
                    <div className={s.notice}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10ZM11 6C11 6.55228 10.5523 7 10 7C9.44772 7 9 6.55228 9 6C9 5.44772 9.44772 5 10 5C10.5523 5 11 5.44772 11 6ZM9 9C8.44772 9 8 9.44772 8 10C8 10.5523 8.44772 11 9 11V14C9 14.5523 9.44772 15 10 15H11C11.5523 15 12 14.5523 12 14C12 13.4477 11.5523 13 11 13V10C11 9.44772 10.5523 9 10 9H9Z"
                                fill="#60A5FA"
                            />
                        </svg>
                        <div className={s.text}>
                            Providers who set advance notice to less than 2 hours may see up to 3× more patient
                            bookings.
                        </div>
                    </div>
                ) : null}

                <div className={s.formSection}>
                    <Form
                        name={formId}
                        form={form}
                        layout="vertical"
                        className={s.form}
                        onFinish={handleFinish}
                        id={formId}
                    >
                        <Form.Item
                            name="advanceNotice"
                            label="Advance notice"
                            rules={[{ required: true, message: 'Please select an advance notice requirement' }]}
                            className={s.formItem}
                        >
                            <AdvanceNotice
                                ignoreValue
                                initialValue={initValue?.minAdvanceBookingHours}
                                ref={advanceNoticeRef}
                            />
                        </Form.Item>
                    </Form>
                </div>
            </div>
        </div>
    );
};

export default Step3;
